<template>
  <div class="flex flex-col max-h-[80vh]">
    <div class="overflow-y-auto px-4 lg:px-8 py-4 lg:py-6">
      <div class="mb-6">
        <label class="block text-sm font-medium text-surface-700 mb-2">
          Modèle d'export <span class="text-red-500">*</span>
        </label>
        <Select
          v-model="selectedExport"
          :options="exportList"
          show-clear
          option-label="name"
          option-value="value"
          option-group-label="label"
          option-group-children="items"
          placeholder="-- Sélectionner un modèle d'export --"
          class="w-full"
        >
          <template #option="slotProps">
            <div class="flex items-center gap-2">
              <i
                v-if="slotProps.option.value < 11"
                class="pi pi-database text-primary"
              />
              <i
                v-else-if="slotProps.option.value >= 11 && slotProps.option.value <= 15"
                class="pi pi-chart-bar text-blue-500"
              />
              <i
                v-else-if="slotProps.option.value === 21"
                class="pi pi-credit-card text-yellow-500"
              />
              <i
                v-else
                class="pi pi-euro text-green-500"
              />
              <span>{{ slotProps.option.name }}</span>
            </div>
          </template>
        </Select>
      </div>

      <Transition
        enter-active-class="transition-all duration-300 ease-in-out"
        enter-from-class="opacity-0 -translate-y-2"
        leave-active-class="transition-all duration-300 ease-in-out"
        leave-to-class="opacity-0 translate-y-2"
      >
        <div
          v-if="selectedExport !== null"
          :class="['grid gap-4 lg:gap-6', hasFilters && hasOptions ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1']"
        >
          <div
            v-if="hasFilters"
            class="space-y-4"
          >
            <Fieldset
              legend="Filtres principaux"
              :toggleable="false"
            >
              <!-- Choix du filtrage des dates pour les abonnements -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['dateFilterSubscriptionList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">
                    Choix de filtrage des dates pour les abonnements
                  </span>
                  <Select
                    v-model="dateFilterSubscriptionValue"
                    :options="dateFilterSubscription"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="Selectionner un type de filtrage de date"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Choix du filtrage des dates pour les factures -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['dateFilterInvoiceList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">
                    Choix de filtrage des dates pour les factures
                  </span>
                  <Select
                    v-model="dateFilterInvoiceValue"
                    :options="dateFilterInvoice"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="Selectionner un type de filtrage de date"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Date de début -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['fromList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">{{ startDateLabel }}</span>
                  <DatePicker
                    v-model="startdate"
                    show-icon
                    :show-on-focus="false"
                    show-button-bar
                    class="w-full"
                  />
                  <Message
                    v-if="errorMessageStartDate"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageStartDate }}
                  </Message>
                </div>
              </Transition>

              <!-- Date de fin -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['toList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">{{ endDateLabel }}</span>
                  <DatePicker
                    v-model="enddate"
                    show-icon
                    :show-on-focus="false"
                    show-button-bar
                    class="w-full"
                  />
                  <Message
                    v-if="errorMessageEndDate"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageEndDate }}
                  </Message>
                </div>
              </Transition>

              <!-- Ville -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['cityList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Ville</span>
                  <modelSearch
                    v-model="selectedCity"
                    api-url="/api/cities"
                    :no-pagination="true"
                    @selection="(id, obj) => city = obj"
                  />
                  <Message
                    v-if="errorMessageCity"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageCity }}
                  </Message>
                </div>
              </Transition>

              <!-- Toutes les villes -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['allCitiesList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Ville</span>
                  <Select
                    v-model="selectedCity"
                    :options="allCities"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="-- Sélectionner une ville --"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                  <Message
                    v-if="errorMessageAllCity"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageAllCity }}
                  </Message>
                </div>
              </Transition>

              <!-- Marché seul -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['marketList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Marché</span>
                  <Select
                    v-model="selectedMarket"
                    :options="allMarkets"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Tous les marchés"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                  <Message
                    v-if="errorMessageMarket"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageMarket }}
                  </Message>
                </div>
              </Transition>

              <!-- Marché multiple -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]?.multipleMarketList && selectedCity > 0">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Marchés</span>
                  <MultiSelect
                    v-model="selectedMarkets"
                    :options="markets"
                    option-label="name"
                    option-value="value"
                    filter
                    display="chip"
                    :placeholder="`Sélectionner des marchés pour la ville de ${cityName[selectedCity]}`"
                    class="w-full text-sm"
                    :max-selected-labels="500"
                    show-clear
                    panel-class="text-sm"
                    :pt="{
                      root: { class: 'flex-wrap items-start py-2 px-3' },
                      label: { class: 'text-sm' },
                      item: { class: 'py-2 px-3 text-sm' },
                      header: { class: 'py-2 px-3 text-sm' },
                      filterInput: { class: 'text-sm py-1' },
                      token: { class: 'text-sm px-2 py-1 m-1 rounded bg-primary-100 text-primary-800' },
                      trigger: { class: 'px-2' },
                    }"
                  />
                  <Message
                    v-if="errorMessageMarkets"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageMarkets }}
                  </Message>
                </div>
              </Transition>

              <!-- Commerçants  -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['merchantList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Commerçant</span>
                  <modelSearch
                    v-model="selectedMerchant"
                    api-url="/api/merchantsNoPagination"
                    no-pagination="true"
                  />
                  <Message
                    v-if="errorMessageMerchant"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageMerchant }}
                  </Message>
                </div>
              </Transition>

              <!-- Entreprises  -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['companyList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Entreprise</span>
                  <modelSearch
                    v-model="selectedCompany"
                    api-url="/api/companiesNoPagination"
                    no-pagination="true"
                  />
                  <Message
                    v-if="errorMessageCompany"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageCompany }}
                  </Message>
                </div>
              </Transition>

              <!-- Placiers -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['placerList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Placier</span>
                  <Select
                    v-model="selectedPlacer"
                    :options="placers"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="Tous les placiers"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                  <Message
                    v-if="errorMessagePlacer"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessagePlacer }}
                  </Message>
                </div>
              </Transition>

              <!-- Caisse -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['cashRegisterList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Caisse</span>
                  <Select
                    v-model="selectedCashRegister"
                    :options="cashRegisters"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Toutes les caisses "
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                  <Message
                    v-if="errorMessageCashRegister"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageCashRegister }}
                  </Message>
                </div>
              </Transition>

              <!-- Périodicités -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['periodicityList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Périodicité</span>
                  <Select
                    v-model="selectedPeriodicity"
                    :options="periodicities"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="-- Sélectionner la périodicité --"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                  <Message
                    v-if="errorMessagePeriodicity"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessagePeriodicity }}
                  </Message>
                </div>
              </Transition>

              <!-- Banques -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['bankAccountList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Banque</span>
                  <Select
                    v-model="selectedBankAccount"
                    :options="bankAccounts"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Toutes les banques"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Type de factures -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['invoiceTypeList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Type de factures</span>
                  <Select
                    v-model="selectedInvoiceType"
                    :options="invoiceTypes"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Tous les types de facture"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Mode de paiement -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['paymentMethodList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Mode de paiement</span>
                  <Select
                    v-model="selectedPaymentMethod"
                    :options="paymentMethods"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Tous les modes de paiement"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Type de signalements -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['eventTypeList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Type de signalements</span>
                  <Select
                    v-model="selectedEventType"
                    :options="eventTypes"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Tous les types de signalement "
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                  <Message
                    v-if="errorMessageEventType"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageEventType }}
                  </Message>
                </div>
              </Transition>

              <!-- Sous-type de signalement -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="showEventSubtypeFilter">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Sous-type de signalement</span>
                  <Select
                    v-model="selectedEventSubtype"
                    :options="availableEventSubtypeOptions"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Tous les sous-types de signalement "
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                  <Message
                    v-if="errorMessageEventSubtype"
                    severity="error"
                    icon="pi pi-times-circle"
                    variant="simple"
                    size="small"
                    class="mt-2"
                  >
                    {{ errorMessageEventSubtype }}
                  </Message>
                </div>
              </Transition>

              <!-- Statut Facture -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['statusInvoiceList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Statut de la facture</span>
                  <Select
                    v-model="selectedInvoiceStatus"
                    :options="invoiceStatus"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Tous les statuts de facture"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Statut Impayés -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['statusInvoiceUnpaidList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Statut de la facture
                    impayée</span>
                  <Select
                    v-model="selectedInvoiceUnpaidStatus"
                    :options="invoiceUnpaidStatus"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Tous les statuts de facture impayée"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Statut Signalement -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['statusEventList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Statut du signalement ou
                    infraction</span>
                  <Select
                    v-model="selectedEventStatus"
                    :options="eventStatus"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Tous les statuts de signalement"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Statut Abonnement -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['statusSubscriptionList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Statut de l'abonnement au {{
                    datenowfr }}</span>
                  <Select
                    v-model="selectedSubscriptionStatus"
                    :options="subscriptionStatus"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Tous les statuts d'abonnement "
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Statut Candidature -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['statusCandidacyList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Statut de la
                    candidature</span>
                  <Select
                    v-model="selectedCandidacyStatus"
                    :options="candidacyStatus"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Tous les statuts de candidature"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Statut Dépôt -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['statusDepositList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Statut du dépôt</span>
                  <Select
                    v-model="selectedDepositStatus"
                    :options="depositStatus"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder=" Tous les statuts de dépôt "
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Absences justifiées -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['absenceJustifiedList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Absences justifiée</span>
                  <Select
                    v-model="justifiedAbsenceValue"
                    :options="justifiedAbsences"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="Toutes les absences"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Code comptable -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['accountancyCodeList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Code comptable</span>
                  <Select
                    v-model="selectedAccountancyCode"
                    :options="accountancyCodes"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="Tous les codes comptables"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>

              <!-- Code comptable Abonné -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div v-if="initiallist[selectedExport]['accountancyCodeSubscribedList']">
                  <span class="block text-sm font-medium leading-6 text-surface-700 mb-2">Code comptable Abonné</span>
                  <Select
                    v-model="selectedAccountancyCodeSubscribed"
                    :options="accountancyCodeSubscribeds"
                    show-clear
                    option-label="name"
                    option-value="value"
                    placeholder="Tous les codes comptable abonnés"
                    class="w-full"
                    :loading="!isDataLoaded"
                  />
                </div>
              </Transition>
            </Fieldset>
          </div>

          <div
            v-if="hasOptions"
            class="space-y-4"
          >
            <Fieldset
              legend="Options d'export"
              :toggleable="false"
            >
              <!-- Avec Articles -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div
                  v-if="initiallist[selectedExport]['withArticleList']"
                  class="flex items-center gap-2"
                >
                  <Checkbox
                    v-model="withArticleValue"
                    input-id="withArticleValue"
                    name="withArticleValue"
                    value="1"
                    binary
                  />
                  <label
                    for="withArticleValue"
                    class="text-sm cursor-pointer"
                  >Avec articles</label>
                </div>
              </Transition>

              <!-- Avoir avec des montants negatif -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div
                  v-if="initiallist[selectedExport]['avoirNegativeList']"
                  class="flex items-center gap-2"
                >
                  <Checkbox
                    v-model="avoirNegativeValue"
                    input-id="avoirNegativeValue"
                    name="avoirNegativeValue"
                    value="1"
                    binary
                  />
                  <label
                    for="avoirNegativeValue"
                    class="text-sm cursor-pointer"
                  >Avoir avec des montants negatif</label>
                </div>
              </Transition>

              <!-- Filter par date de paiement -->
              <Transition
                enter-active-class="transition-all duration-300 ease-in-out"
                enter-from-class="opacity-0 -translate-y-2"
                leave-active-class="transition-all duration-300 ease-in-out"
                leave-to-class="opacity-0 translate-y-2"
              >
                <div
                  v-if="initiallist[selectedExport]['paymentDateFilterList']"
                  class="flex items-center gap-2"
                >
                  <Checkbox
                    v-model="paymentDateFilterValue"
                    input-id="paymentDateFilterValue"
                    name="paymentDateFilterValue"
                    value="1"
                    binary
                  />
                  <label
                    for="paymentDateFilterValue"
                    class="text-sm cursor-pointer"
                  >Filter par date de paiement</label>
                </div>
              </Transition>
            </Fieldset>
          </div>
        </div>
      </Transition>
    </div>

    <Toast />

    <div
      class="sticky bottom-0 flex justify-end gap-3 px-4 lg:px-8 pt-4 pb-6 border-t border-surface-200 bg-surface-50/95 backdrop-blur-sm"
    >
      <Button
        label="Annuler"
        text
        @click="closeDialog"
      />
      <Button
        v-if="selectedExport !== null && initiallist[selectedExport]?.['exportButton']"
        label="Export Excel"
        severity="info"
        icon="pi pi-download"
        :loading="!isDataLoaded || isExportingExcel"
        :disabled="isExportingExcel"
        @click="fetchExcelFile"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ref, computed, onMounted, watch, inject, type Ref } from "vue";
import { format } from "date-fns";
import { useToast } from "primevue/usetoast";

import Button from "primevue/button";
import Select from "primevue/select";
import DatePicker from "primevue/datepicker";
import Checkbox from "primevue/checkbox";
import MultiSelect from "primevue/multiselect";
import Fieldset from "primevue/fieldset";
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";

import ModelSearch from "~/components/atoms/model/search";
import { downloadFileFromBlob } from "~/utils/download";
import { useSanctumClient } from "#imports"; // ✅ Vérifie l'import correct si ce hook est custom
import {
  EVENT_TYPE_INFRACTION,
  fetchEventSubtypes,
} from "~/services/events/catalog";

import { InvoiceTypeEnum } from "~/types/Invoice";

const dialogRef = inject<Ref<DynamicDialogInstance>>("dialogRef");
const closeDialog = () => {
  dialogRef?.value?.close();
};

const toast = useToast();
const $apiFetch = useSanctumClient();

const isExportingExcel = ref(false);

const datenowfr = format(new Date(), "dd-MM-yyyy");

const selectedExport = ref<number | null>(null);
const selectedCity = ref<number | null>(null);
const selectedMarket = ref<string>("");
const selectedMarkets = ref<{ name: string; value: number }[]>([]);
const selectedPeriodicity = ref<number | null>(null);
const selectedPlacer = ref<string>("");
const selectedMerchant = ref<string>("");
const selectedCompany = ref<string>("");
const selectedCashRegister = ref<string>("");

const selectedBankAccount = ref<number | null>(null);
const selectedInvoiceType = ref<string>("");
const selectedPaymentMethod = ref<string>("");
const selectedEventSubtype = ref<number | null>(null);
const selectedEventType = ref<number | null>(null);

const selectedAccountancyCode = ref<number | null>(null);
const selectedAccountancyCodeSubscribed = ref<number | null>(null);

const selectedInvoiceStatus = ref<string>("");
const selectedInvoiceUnpaidStatus = ref<string>("");
const selectedEventStatus = ref<string>("");
const selectedCandidacyStatus = ref<string>("");
const selectedSubscriptionStatus = ref<string>("");
const selectedDepositStatus = ref<number | null>(null);

const justifiedAbsenceValue = ref<number | null>(null);
const dateFilterSubscriptionValue = ref<number | null>(null);
const dateFilterInvoiceValue = ref<number | null>(null);

const withArticleValue = ref(0);
const avoirNegativeValue = ref(0);
const paymentDateFilterValue = ref(0);

const startdate = ref<Date | null>(null); // Date par défaut pour le début
const enddate = ref<Date | null>(null); // Date par défaut pour la fin*

// const startdateformat = computed(() => format(new Date(startdate.value), 'yyyy-MM-dd'));
// const enddateformat = computed(() => format(new Date(enddate.value), 'yyyy-MM-dd'));
// const startdateformatfr = computed(() => format(new Date(startdate.value), 'dd-MM-yyyy'));
// const enddateformatfr = computed(() => format(new Date(enddate.value), 'dd-MM-yyyy'));

const startdateformat = computed(() => {
  if (!startdate.value) return "";
  const date = new Date(startdate.value);
  return isNaN(date.getTime()) ? "" : format(date, "yyyy-MM-dd");
});

const enddateformat = computed(() => {
  if (!enddate.value) return "";
  const date = new Date(enddate.value);
  return isNaN(date.getTime()) ? "" : format(date, "yyyy-MM-dd");
});

const startdateformatfr = computed(() => {
  if (!startdate.value) return "";
  const date = new Date(startdate.value);
  return isNaN(date.getTime()) ? "" : format(date, "dd-MM-yyyy");
});

const enddateformatfr = computed(() => {
  if (!enddate.value) return "";
  const date = new Date(enddate.value);
  return isNaN(date.getTime()) ? "" : format(date, "dd-MM-yyyy");
});

// City & Market Tableau
const allCities = ref<{ name: string; value: number }[]>([]);
const markets = ref<{ name: string; value: number }[]>([]);
const allMarkets = ref<{ name: string; value: number }[]>([]);
const placers = ref<{ name: string; value: number }[]>([]);
const cashRegisters = ref<{ name: string; value: number }[]>([]);
const cityName = ref<Record<number, string>>({});
const placerName = ref<Record<number, string>>({});

const eventTypes = ref<{ name: string; value: number }[]>([]);
const eventSubtypes = ref<{ name: string; value: number; eventTypeId: number }[]>([]);
const invoiceTypes = ref<{ name: string; value: number }[]>([]);
const paymentMethods = ref<{ name: string; value: number }[]>([]);
const bankAccounts = ref<{ name: string; value: number }[]>([]);

const eventStatus = ref<{ name: string; value: number }[]>([]);
const candidacyStatus = ref<{ name: string; value: number }[]>([]);
const subscriptionStatus = ref<{ name: string; value: number }[]>([]);
const invoiceStatus = ref<{ name: string; value: number }[]>([]);
const invoiceUnpaidStatus = ref<{ name: string; value: number }[]>([]);
const depositStatus = ref<{ name: string; value: number }[]>([]);

const accountancyCodes = ref<{ name: string; value: number }[]>([]);
const accountancyCodeSubscribeds = ref<{ name: string; value: number }[]>([]);

// Chargement des composants
const isDataLoaded = ref(false);

// Message d'erreurs
const errorMessageStartDate = ref("");
const errorMessageEndDate = ref("");
const errorMessageCity = ref("");
const errorMessageAllCity = ref("");
const errorMessageMarket = ref("");
const errorMessageMarkets = ref("");
const errorMessagePeriodicity = ref("");
const errorMessagePlacer = ref("");
const errorMessageMerchant = ref("");
const errorMessageCompany = ref("");
const errorMessageEventSubtype = ref("");
const errorMessageEventType = ref("");
const stopErrorExport = ref(false);
const showEventSubtypeFilter = computed(() => (
  Boolean(selectedExport.value)
  && initiallist[selectedExport.value]?.infractionTypeList
  && ((selectedExport.value === 1 && Boolean(selectedEventType.value)) || selectedExport.value === 2)
));
const availableEventSubtypeOptions = computed(() => {
  if (selectedExport.value === 2) {
    return eventSubtypes.value.filter(item => item.eventTypeId === EVENT_TYPE_INFRACTION);
  }

  if (selectedEventType.value) {
    return eventSubtypes.value.filter(
      item => item.eventTypeId === selectedEventType.value,
    );
  }

  return eventSubtypes.value;
});
watch(selectedEventType, () => {
  selectedEventSubtype.value = null;
});

// fetch markets when city changes
const loadMarkets = async () => {
  if (!selectedCity.value || selectedCity.value <= 0) {
    markets.value = [];
    selectedMarkets.value = [];
    return;
  }

  try {
    const response = await $apiFetch(`/api/cities/${selectedCity.value}/markets`, {
      query: { no_pagination: true },
    });

    markets.value = response.data.map((item: any) => ({
      name: item.name,
      value: item.id,
    })).sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }));

    selectedMarkets.value = markets.value.map(m => m.value);
  }
  catch (error) {
    console.error("Erreur chargement marchés :", error);
  }
};

onMounted(async () => {
  try {
    // Chargement des données initiales

    const response = await $apiFetch("/api/cities", {
      query: { no_pagination: true },
    });

    allCities.value = [{ name: "Toutes les villes", value: -2 }, ...response.data.map((item: any) => ({
      name: item.name,
      value: item.id,
    }))];

    cityName.value = response.data.reduce((acc: Record<number, string>, item: any) => {
      acc[item.id] = item.name;
      return acc;
    }, {});

    cityName.value[-2] = "Toutes_Les_Villes";

    // Refonte permissions : lookup scopé (rôle Placier, actifs uniquement) — le suffixe
    // de rôles du libellé devient redondant et disparaît ; l'index users reste protégé.
    const response2 = await $apiFetch("/api/lookup/placers?limit=500");

    placerName.value = response2.data.reduce((acc: Record<number, string>, item: any) => {
      acc[item.id] = item.label;
      return acc;
    }, {});

    placers.value = response2.data
      .filter((item: any) => item.id > 0)
      .map((item: any) => ({
        name: item.label,
        value: item.id,
      })).sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }));

    const response3 = await $apiFetch("/api/markets", {
      query: { no_pagination: true },
    });

    allMarkets.value = response3.data
      .filter((item: any) => item.enable === 1)
      .map((item: any) => ({
        name: `${item.name} - ${cityName.value[item.city_id]}`,
        value: item.id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }));

    const response4 = await $apiFetch("/api/eventTypes", {
      query: { no_pagination: true },
    });

    eventTypes.value = response4.data.map((item: any) => ({
      name: item.name,
      value: item.id,
    }));

    const response5 = await fetchEventSubtypes($apiFetch as any, { grouped: true }) as any;

    eventSubtypes.value = Object.values(response5.event_subtypes ?? {})
      .flat()
      .map((item: any) => ({
        name: item.name,
        value: item.id,
        eventTypeId: item.event_type_id,
      }))
      .sort((left, right) => left.name.localeCompare(right.name, "fr", { sensitivity: "base" }));

    const response6 = await $apiFetch("/api/status", {

    });

    eventStatus.value = response6.data
      .filter((item: any) => item.model_class === "App\\Models\\Event" && Number(item.id) !== 28)
      .map((item: any) => ({
        name: item.label,
        value: item.id,
      }));

    subscriptionStatus.value = response6.data
      .filter((item: any) => item.model_class === "App\\Models\\Subscription")
      .map((item: any) => ({
        name: item.label,
        value: item.id,
      }));

    candidacyStatus.value = response6.data
      .filter((item: any) => item.model_class === "App\\Models\\Candidacy")
      .map((item: any) => ({
        name: item.label,
        value: item.id,
      }));

    invoiceStatus.value = [
      { name: "Payée ou payée partiellement", value: -1 },
      ...response6.data
        .filter((item: any) => item.model_class === "App\\Models\\Invoice")
        .map((item: any) => ({
          name: item.label,
          value: item.id,
        })),
    ];

    justifiedAbsences.value = response6.data
      .filter((item: any) => item.model_class === "App\\Models\\JustifiedAbsence")
      .map((item: any) => ({
        name: item.label,
        value: item.id,
      }));

    invoiceUnpaidStatus.value = response6.data
      .filter((item: any) => [16, 17, 18, 19].includes(item.id))
      .map((item: any) => ({
        name: item.label,
        value: item.id,
      }));

    depositStatus.value = response6.data
      .filter((item: any) => item.model_class === "App\\Models\\Deposit")
      .map((item: any) => ({
        name: item.label,
        value: item.id,
      }));

    const response7 = await $apiFetch("/api/invoiceTypes/search", {
      query: { no_pagination: true },
      method: "post",
      body: {
        filters: [
          {
            field: "id",
            operator: "in",
            value: [InvoiceTypeEnum.Subscription, InvoiceTypeEnum.Ticket,
              InvoiceTypeEnum.Facture, InvoiceTypeEnum.Credit],
          },
        ],
      },
    });

    invoiceTypes.value = response7.data
      .filter((item: any) => item.id != 3)
      .map((item: any) => ({
        name: item.description,
        value: item.id,
      }));

    const response8 = await $apiFetch("/api/paymentMethods", {
      query: { no_pagination: true },
    });

    paymentMethods.value = response8.data
      .map((item: any) => ({
        name: item.name,
        value: item.id,
      }));

    const response9 = await $apiFetch("/api/cashRegisters", {
      query: { no_pagination: true },
    });

    cashRegisters.value = response9.data
      .map((item: any) => ({
        name: `${cityName.value[item.city_id]} - Caisse ${item.id} - ${placerName.value[item.placer_id]}`,
        value: item.id,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "fr", { sensitivity: "base" }));

    const response10 = await $apiFetch("/api/accountingCodes", {
      query: { no_pagination: true },
    });

    accountancyCodes.value = response10.data
      .filter((item: any) => item.code_type == 1)
      .map((item: any) => ({
        name: `${item.name} - ${item.description}`,
        value: item.id,
      }));

    accountancyCodeSubscribeds.value = response10.data
      .filter((item: any) => item.code_type == 2)
      .map((item: any) => ({
        name: `${item.name} - ${item.description}`,
        value: item.id,
      }));

    const response11 = await $apiFetch("/api/bankAccounts", {
      query: { no_pagination: true },
    });

    bankAccounts.value = response11.data
      .map((item: any) => ({
        name: item.name,
        value: item.id,
      }));

    // ✅ Tous les chargements terminés
    isDataLoaded.value = true;
  }
  catch (e) {
    console.error("Erreur chargement villes :", e);
  }

  loadMarkets();
});

watch(selectedCity, loadMarkets);

const hasFilters = computed(() => {
  if (selectedExport.value === null) return false;
  const config = initiallist[selectedExport.value];
  if (!config) return false;
  return config.fromList || config.toList || config.dateFilterSubscriptionList || config.dateFilterInvoiceList
    || config.cityList || config.allCitiesList || config.marketList || config.multipleMarketList
    || config.merchantList || config.companyList || config.placerList || config.cashRegisterList
    || config.periodicityList || config.bankAccountList || config.invoiceTypeList || config.paymentMethodList
    || config.eventTypeList || config.infractionTypeList || config.statusInvoiceList || config.statusInvoiceUnpaidList
    || config.statusEventList || config.statusSubscriptionList || config.statusCandidacyList || config.statusDepositList
    || config.absenceJustifiedList || config.accountancyCodeList || config.accountancyCodeSubscribedList;
});

const hasOptions = computed(() => {
  if (selectedExport.value === null) return false;
  const config = initiallist[selectedExport.value];
  if (!config) return false;
  return config.withArticleList || config.avoirNegativeList || config.paymentDateFilterList;
});

const fetchExcelFile = async () => {
  stopErrorExport.value = false; // reset à chaque appel
  //   if (initiallist[selectedExport.value]?.fromList === true) {
  //       if (!startdate.value) {
  //         errorMessageStartDate.value = 'Veuillez renseigner la date de début.';
  //         stopErrorExport.value = true;
  //       } else {
  //         errorMessageStartDate.value = '';
  //       }
  //   }
  //
  //   if (initiallist[selectedExport.value]?.toList === true) {
  //       if (!enddate.value) {
  //         errorMessageEndDate.value = 'Veuillez renseigner la date de fin.';
  //         stopErrorExport.value = true;
  //       } else {
  //         errorMessageEndDate.value = '';
  //       }
  //   }
  //
  //   if (initiallist[selectedExport.value]?.cityList === true) {
  //       if (!selectedCity.value && !(selectedExport.value === 6)) {
  //         errorMessageCity.value = 'Veuillez sélectionner une ville.';
  //         stopErrorExport.value = true;
  //       } else {
  //         errorMessageCity.value = '';
  //       }
  //   }
  //
  //   if (initiallist[selectedExport.value]?.allCitiesList === true) {
  //       if (!selectedCity.value) {
  //         errorMessageAllCity.value = 'Veuillez sélectionner une ville.';
  //         stopErrorExport.value = true;
  //       } else {
  //         errorMessageAllCity.value = '';
  //       }
  //   }
  //
  //   if (initiallist[selectedExport.value]?.marketList === true) {
  //       if (!selectedMarket.value || selectedMarket.value === '') {
  //         errorMessageMarket.value = 'Veuillez sélectionner un marché.';
  //         stopErrorExport.value = true;
  //       } else {
  //         errorMessageMarket.value = '';
  //       }
  //   }
  //
  //   if (initiallist[selectedExport.value]?.multipleMarketList === true) {
  //       if((!selectedMarkets.value || selectedMarkets.value.length === 0)) {
  //         errorMessageMarkets.value = 'Veuillez sélectionner au moins un marché.';
  //         stopErrorExport.value = true;
  //       } else {
  //         errorMessageMarkets.value = '';
  //       }
  //   }
  //
  //   if (initiallist[selectedExport.value]?.periodicityList === true ) {
  //       if (!selectedPeriodicity.value ) {
  //         errorMessagePeriodicity.value = 'Veuillez sélectionner une périodicité.';
  //         stopErrorExport.value = true;
  //       } else {
  //         errorMessagePeriodicity.value = '';
  //       }
  //   }
  //
  //   if (initiallist[selectedExport.value]?.placerList === true) {
  //       if (!selectedPlacer.value || selectedPlacer.value === '') {
  //         errorMessagePlacer.value = 'Veuillez sélectionner un placier.';
  //         stopErrorExport.value = true;
  //       } else {
  //         errorMessagePlacer.value = '';
  //       }
  //   }

  // Interruption si une erreur est présente
  if (stopErrorExport.value === true) {
    return;
  }

  try {
    isExportingExcel.value = true;

    const params = new URLSearchParams();

    // Helper : n'ajoute le paramètre que si la valeur est renseignée (non null, non vide)
    // Quand un filtre est à sa position par défaut ("Tous"), on n'envoie pas le paramètre
    // afin que le backend traite l'absence comme "pas de filtre" = "All"
    const appendIfPresent = (key: string, value: string | number | null | undefined) => {
      if (value !== null && value !== undefined && value !== "") {
        params.append(key, value.toString());
      }
    };

    // Type d'export (toujours requis)
    params.append("type", selectedExport.value?.toString() ?? "");

    // Filtres de date (optionnels)
    appendIfPresent("dateFilterSubscriptionValue", dateFilterSubscriptionValue.value);
    appendIfPresent("dateFilterInvoiceValue", dateFilterInvoiceValue.value);
    appendIfPresent("from", startdateformat.value);
    appendIfPresent("to", enddateformat.value);

    // Ville : -2 = "Toutes les villes", null = pas de sélection → les deux = pas de filtre
    if (selectedCity.value && selectedCity.value > 0) {
      params.append("cityId", selectedCity.value.toString());
    }

    // Filtres entités (envoyés uniquement si une valeur spécifique est choisie)
    appendIfPresent("marketId", selectedMarket.value);
    appendIfPresent("periodicityId", selectedPeriodicity.value);
    appendIfPresent("placerId", selectedPlacer.value);
    appendIfPresent("merchantId", selectedMerchant.value);
    appendIfPresent("companyId", selectedCompany.value);
    appendIfPresent("cashRegisterId", selectedCashRegister.value);

    appendIfPresent("invoiceTypeId", selectedInvoiceType.value);
    appendIfPresent("eventTypeId", selectedEventType.value);
    appendIfPresent("eventSubtypeId", selectedEventSubtype.value);
    appendIfPresent("infractionTypeId", selectedEventSubtype.value);
    appendIfPresent("paymentMethodId", selectedPaymentMethod.value);
    appendIfPresent("bankAccountId", selectedBankAccount.value);

    appendIfPresent("accountancyCodeId", selectedAccountancyCode.value);
    appendIfPresent("accountancyCodeSubscribedId", selectedAccountancyCodeSubscribed.value);

    // Filtres statuts (envoyés uniquement si une valeur spécifique est choisie)
    appendIfPresent("invoiceStatusId", selectedInvoiceStatus.value);
    appendIfPresent("invoiceUnpaidStatusId", selectedInvoiceUnpaidStatus.value);
    appendIfPresent("eventStatusId", selectedEventStatus.value);
    appendIfPresent("subscriptionStatusId", selectedSubscriptionStatus.value);
    appendIfPresent("candidacyStatusId", selectedCandidacyStatus.value);
    appendIfPresent("depositStatusId", selectedDepositStatus.value);

    appendIfPresent("justifiedAbsenceId", justifiedAbsenceValue.value);

    // Cases à cocher (flags booléens, toujours envoyés)
    params.append("withArticleValue", withArticleValue.value?.toString() ?? "0");
    params.append("avoirNegativeValue", avoirNegativeValue.value?.toString() ?? "0");
    params.append("paymentDateFilterValue", paymentDateFilterValue.value?.toString() ?? "0");

    // Marchés multiples : envoyés uniquement si une sélection spécifique existe
    if (selectedMarkets.value && selectedMarkets.value.length > 0) {
      selectedMarkets.value.forEach((id) => {
        params.append("marketIds[]", id.toString());
      });
    }

    const blob = await $apiFetch("/api/exportnormal?" + params.toString(), {
      method: "GET",
    });

    console.log(blob);

    // Nom du fichier
    const exportTable = {
      1: "Signalements",
      2: "Infractions",
      3: "Absences",
      4: "Période_Absences",
      5: "Présences_Absences",
      6: "Abonnements",
      7: "Taux_Occupation",
      8: "Candidatures",
      9: "Commerçants",
      10: "Contacts",
      11: "Recapitulatif_Placier",
      12: "Recapitulatif_Commerçant",
      13: "Recapitulatif_Entreprise",
      14: "Recapitulatif_Marché",
      15: "Recapitulatif_Ville",
      16: "Factures",
      17: "Paiements",
      18: "Impayés",
      19: "Articles",
      20: "Dépôts",
      21: "Factures_2",
    };

    const cityLabel = (selectedCity.value && (initiallist[selectedExport.value]?.allCitiesList === true || initiallist[selectedExport.value]?.cityList === true)) ? `_${cityName.value[selectedCity.value]}` : "";
    const placerLabel = (selectedPlacer.value && initiallist[selectedExport.value]?.placerList === true) ? `_${placerName.value[selectedPlacer.value]}` : "";
    const startDateLabel = (startdateformatfr.value && initiallist[selectedExport.value]?.fromList === true) ? `_${startdateformatfr.value}` : "";
    const endDateLabel = (enddateformatfr.value && initiallist[selectedExport.value]?.toList === true) ? `_${enddateformatfr.value}` : "";
    const withArticleLabel = (withArticleValue.value === 1 && initiallist[selectedExport.value]?.withArticleList === true) ? "_Avec_Articles" : "";
    const fileName
      = `${exportTable[selectedExport.value!] ?? "Export"}`
        + cityLabel
        + placerLabel
        + startDateLabel
        + endDateLabel
        + withArticleLabel;

    downloadFileFromBlob(blob, fileName);

    toast.add({ severity: "success", summary: "Export réussi", life: 8000 });
  }
  catch (error) {
    console.error("Erreur lors de l'export Excel :", error);
    toast.add({ severity: "error", summary: "Erreur export", detail: "Impossible de générer le fichier", life: 8000 });
  }
  finally {
    isExportingExcel.value = false;
  }
};

// Liste des exports
const exportList = [
  {
    label: "General",
    items: [
      { name: "Signalements", value: 1 },
      { name: "Infractions", value: 2 },
      { name: "Absences", value: 3 },
      { name: "Période d'absences", value: 4 },
      { name: "Présences - Absences", value: 5 },
      { name: "Nouveaux Abonnements - Résiliation", value: 6 },
      // { name: 'Taux d\'occupation', value: 7 },
      { name: "Candidatures", value: 8 },
      { name: "Commerçants", value: 9 },
      { name: "Contacts", value: 10 },
    ],
  },
  {
    label: "Récapitulatif",
    items: [
      { name: "Récapitulatif - Placier", value: 11 },
      { name: "Récapitulatif - Commerçant", value: 12 },
      { name: "Récapitulatif - Entreprise", value: 13 },
      { name: "Récapitulatif - Marché", value: 14 },
      { name: "Récapitulatif - Ville", value: 15 },
    ],
  },
  {
    label: "Comptabilité",
    items: [
      { name: "Factures", value: 16 },
      { name: "Paiements", value: 17 },
      { name: "Impayés", value: 18 },
      { name: "Articles", value: 19 },
      { name: "Dépôts", value: 20 },
    ],
  },
  {
    label: "Chiffre d'affaires",
    items: [
      { name: "Factures (2)", value: 21 },
    ],
  },
];

const periodicities = [
  { name: "Sans / Tenue de marché", value: 1 },
  { name: "Semaine", value: 2 },
  { name: "Double semaine", value: 3 },
  { name: "Mois", value: 4 },
  { name: "Trimestre", value: 5 },
  { name: "Année", value: 6 },
  { name: "Par date de période de facturation", value: 7 },
];

const justifiedAbsences = [
  { name: "Toutes les absences", value: 1 },
  { name: "Toutes les absences justifiées", value: 2 },
  { name: "Toutes les absences non justifiées", value: 3 },
];

const dateFilterSubscription = [
  { name: "Par date de création d'abonnement", value: 1 },
  { name: "Par date de début d'abonnement", value: 2 },
  { name: "Par date de fin d'abonnement", value: 3 },
];

const dateFilterInvoice = [
  { name: "Par date d'émission", value: 1 },
  { name: "Par date de paiement", value: 2 },
  { name: "Par date de début de période facturation", value: 3 },
  // { name: 'Par date de fin de période facturation', value: 4 },
  { name: "Par date de période facturation", value: 5 },
];

const initiallist = {
  1: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: true, merchantList: true, companyList: false, allCitiesList: true, multipleMarketList: true, exportButton: true, eventTypeList: true, infractionTypeList: true, statusEventList: true, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  2: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: true, merchantList: true, companyList: false, allCitiesList: true, multipleMarketList: true, exportButton: true, eventTypeList: false, infractionTypeList: true, statusEventList: true, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  3: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: true, companyList: false, allCitiesList: true, multipleMarketList: true, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: true, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  4: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: true, merchantList: true, companyList: false, allCitiesList: true, multipleMarketList: true, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  5: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: true, companyList: false, allCitiesList: true, multipleMarketList: true, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  6: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: true, companyList: false, allCitiesList: true, multipleMarketList: true, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: true, dateFilterSubscriptionList: true, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  7: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: false, companyList: false, allCitiesList: true, multipleMarketList: true, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  8: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: true, companyList: false, allCitiesList: true, multipleMarketList: true, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: true, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  9: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: true, companyList: false, allCitiesList: false, multipleMarketList: false, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  10: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: false, companyList: false, allCitiesList: false, multipleMarketList: false, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  11: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: true, merchantList: false, companyList: false, allCitiesList: false, multipleMarketList: false, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  12: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: true, companyList: false, allCitiesList: false, multipleMarketList: false, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  13: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: false, companyList: true, allCitiesList: false, multipleMarketList: false, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  14: { fromList: true, toList: true, cityList: false, marketList: true, periodicityList: false, placerList: false, merchantList: false, companyList: false, allCitiesList: false, multipleMarketList: false, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  15: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: false, companyList: false, allCitiesList: true, multipleMarketList: false, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  16: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: true, merchantList: true, companyList: true, allCitiesList: true, multipleMarketList: true, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: true, invoiceTypeList: true, dateFilterInvoiceList: true, withArticleList: true, avoirNegativeList: true, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  17: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: true, merchantList: true, companyList: true, allCitiesList: false, multipleMarketList: false, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: true, cashRegisterList: true, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  18: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: true, merchantList: true, companyList: true, allCitiesList: true, multipleMarketList: true, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: true, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: true, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  19: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: false, companyList: false, allCitiesList: true, multipleMarketList: true, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: true, accountancyCodeSubscribedList: true, bankAccountList: false, statusDepositList: false, paymentDateFilterList: false },
  20: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: true, merchantList: false, companyList: false, allCitiesList: false, multipleMarketList: false, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: false, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: true, cashRegisterList: true, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: true, statusDepositList: true, paymentDateFilterList: false },
  21: { fromList: true, toList: true, cityList: false, marketList: false, periodicityList: false, placerList: false, merchantList: false, companyList: false, allCitiesList: true, multipleMarketList: false, exportButton: true, eventTypeList: false, infractionTypeList: false, statusEventList: false, absenceJustifiedList: false, statusSubscriptionList: false, dateFilterSubscriptionList: false, statusCandidacyList: false, statusInvoiceList: false, invoiceTypeList: true, dateFilterInvoiceList: false, withArticleList: false, avoirNegativeList: false, paymentMethodList: false, cashRegisterList: false, statusInvoiceUnpaidList: false, accountancyCodeList: false, accountancyCodeSubscribedList: false, bankAccountList: false, statusDepositList: false, paymentDateFilterList: true },
};

const _messageExplanation = computed(() => {
  const type = selectedExport.value;

  if (!type) return "Selectionnez un type de reporting.";

  const labelsmesage: Record<string, string> = {
    // placier: 'Cet export contient la liste des placiers.<ul><li>2 lignes par placier (une pour chèque et une pour espèce)</li><li>Colonnes à afficher :</li><li>compte : combinaison de 530 pour espèce et 531 pour chèque puis l’ID unique du placier</li><li>placier : nom et prénom du placier</li><li>type de paiement : espèce ou chèque</li></ul>',
    1: `Export Signalements`,
    2: `Export Infractions`,
    3: `Export Absences`,
    4: `Export Période d'absences`,
    5: `Export Présences - Absences`,
    6: `Export Nouveaux Abonnements - Résiliation`,
    7: `Export Taux d'occupation`,
    8: `Export Candidatures`,
    9: `Export Commerçants`,
    10: `Export Contacts`,
    11: `Export Récapitulatif - Placier`,
    12: `Export Récapitulatif - Commerçant`,
    13: `Export Récapitulatif - Entreprise`,
    14: `Export Récapitulatif - Marché`,
    15: `Export Récapitulatif - Ville`,
    16: `Export Factures`,
    17: `Export Paiements`,
    18: `Export Impayés`,
    19: `Export Articles`,
    20: `Export Dépôts`,
    21: `Export Factures (2)`,
  };

  return labelsmesage[type] ?? "defaut test";
});

const message1 = computed(() => {
  return "Cette option permet d'afficher les présences des abonnés pour le reporting complet.";
});

const startDateLabel = computed(() => {
  const type = selectedExport.value;

  if (!type) return "Date de début";

  if (type === 6) {
    if (!dateFilterSubscriptionValue.value)
      return "Début date de création de l'abonnement";
    else {
      if (dateFilterSubscriptionValue.value === 1)
        return "Début date de création de l'abonnement";
      else {
        if (dateFilterSubscriptionValue.value === 2)
          return "Début date de début d'abonnement";
        else
          return "Début date de fin d'abonnement";
      }
    }
  }

  if (type === 16) {
    switch (dateFilterInvoiceValue.value) {
      case 1:
        return "Début date d'émission des factures";
      case 2:
        return "Début date de paiement des factures";
      case 3:
        return "Début date de début des périodes de facturation des factures ";
      case 4:
        return "Début date de fin des périodes de facturation des factures ";
      case 5:
        return "Début date de période de facturation des factures";
      default:
        return "Début date d'émission des factures";
    }
  }

  if (type === 21) {
    if (!paymentDateFilterValue.value)
      return "Début date d'émission des factures";
    else {
      if (paymentDateFilterValue.value == 1)
        return "Début date de paiement des factures";
      else
        return "Début date d'émission des factures";
    }
  }

  const labels: Record<string, string> = {
    1: "Début date de création des signalements",
    2: "Début date de création des infractions",
    3: "Début date de tenue de marché",
    4: "Début date de début des périodes d'absence",
    5: "Début date des tenue de marché",
    7: "Début date",
    8: "Début date de création des candidatures",
    9: "Début date de création des commerçants",
    10: "Début date de création des contacts",
    11: "Début date",
    12: "Début date",
    13: "Début date",
    14: "Début date",
    15: "Début date",
    17: "Début date de création des paiements",
    18: "Début date d'émission des factures",
    19: "Début date de départ des articles",
    20: "Début date de création des dépôts",
  };

  return labels[type] ?? "Date de début";
});

const endDateLabel = computed(() => {
  const type = selectedExport.value;

  if (!type) return "Date de fin";

  if (type === 6) {
    if (!dateFilterSubscriptionValue.value)
      return "Fin date de création de l'abonnement";
    else {
      if (dateFilterSubscriptionValue.value === 1)
        return "Fin date de création de l'abonnement";
      else {
        if (dateFilterSubscriptionValue.value === 2)
          return "Fin date de début d'abonnement";
        else
          return "Fin date de fin d'abonnement";
      }
    }
  }

  if (type === 16) {
    switch (dateFilterInvoiceValue.value) {
      case 1:
        return "Fin date d'émission des factures";
      case 2:
        return "Fin date de paiement des factures";
      case 3:
        return "Fin date de début des périodes de facturation des factures ";
      case 4:
        return "Fin date de fin des périodes de facturation des factures ";
      case 5:
        return "Fin date de période de facturation des factures";
      default:
        return "Fin date d'émission des factures";
    }
  }

  if (type === 21) {
    if (!paymentDateFilterValue.value)
      return "Fin date d'émission des factures";
    else {
      if (paymentDateFilterValue.value == 1)
        return "Fin date de paiement des factures";
      else
        return "Fin date d'émission des factures";
    }
  }

  const labelends: Record<string, string> = {
    1: "Fin date de création des signalements",
    2: "Fin date de création des infractions",
    3: "Fin date de tenue de marché",
    4: "Fin date de début des périodes d'absence",
    5: "Fin date des tenue de marché",
    7: "Fin date",
    8: "Fin date de création des candidatures",
    9: "Fin date de création des commerçants",
    10: "Fin date de création des contacts",
    11: "Fin date",
    12: "Fin date",
    13: "Fin date",
    14: "Fin date",
    15: "Fin date",
    17: "Fin date de création des paiements",
    18: "Fin date d'émission des factures",
    19: "Fin date de départ des articles",
    20: "Fin date de création des dépôts",
  };

  return labelends[type] ?? "Date de fin";
});
</script>

<style scoped>
.button {
  padding: 10px 20px;
  background-color: #007bff;
  color: #fff;
  border: none;
  border-radius: 7px;
  cursor: pointer;
}

.button:hover {
  background-color: #0056b3;
}

/* Dialog position */
::v-deep(.p-dialog) {
  position: fixed !important;
  top: 10px !important;
  right: 10px !important;
}

/* Tooltip (doit être en deep car non scoped) */
::v-deep(.p-tooltip) {
  background-color: rgba(0, 0, 0, 0.85);
  color: white !important;
  border-radius: 0.5rem;
  padding: 0.5rem 1rem;
  font-size: 0.875rem;
}

/* Message d'erreur */
::v-deep(.p-message-error) {
  background-color: #f8d7da;
  border: 1px solid #f5c2c7;
  color: #842029;
  padding: 0.2rem;
  border-radius: 2px;
  display: flex;
  align-items: center;
  font-size: 0.95rem;
}

::v-deep(.p-message-error i) {
  font-size: 1.1rem;
}

/* Groupe d'éléments du Select */
::v-deep(.p-select-list > .p-select-option-group) {
  background-color: #f0f0f0;
  color: #333;
  font-weight: 800;
  padding: 0.5rem 1rem;
  font-size: 1.25rem;
}

/* Élément individuel du Select */
::v-deep(.p-select-option) {
  height: 3rem;
  line-height: 3rem;
  font-size: 1.25rem;
}

/* Applique la hauteur maximale au panneau déroulant */
::v-deep(.p-select-overlay) {
  max-height: 90vh !important;
  height: auto !important;
  overflow-y: auto !important;
}

/* Optionnel : gérer le scroll interne */
::v-deep(.p-select-list) {
  overflow-y: auto;
}
</style>
